import { useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  CreateQuestionBankEntryInput,
  IQuestionBankEntryDetail,
} from '@brushfire/types'
import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'
import { questionBankKeys } from '@/features/question-bank/api/question-bank-queries'

export async function createQuestionBankEntry(
  input: CreateQuestionBankEntryInput,
): Promise<IQuestionBankEntryDetail> {
  const { data: envelope } = await apiClient.post<ApiResponse<IQuestionBankEntryDetail>>(
    '/api/v1/question-bank',
    input,
  )
  if (!envelope?.data?.id) {
    throw new Error('Question bank create response missing data')
  }
  return envelope.data
}

export async function updateQuestionBankEntry(
  id: string,
  input: CreateQuestionBankEntryInput,
): Promise<IQuestionBankEntryDetail> {
  const { data: envelope } = await apiClient.put<ApiResponse<IQuestionBankEntryDetail>>(
    `/api/v1/question-bank/${encodeURIComponent(id)}`,
    input,
  )
  if (!envelope?.data?.id) {
    throw new Error('Question bank update response missing data')
  }
  return envelope.data
}

export async function deleteQuestionBankEntry(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/question-bank/${encodeURIComponent(id)}`)
}

export function useCreateQuestionBankEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createQuestionBankEntry,
    onSuccess: () => {
      Promise.resolve(qc.invalidateQueries({ queryKey: questionBankKeys.all })).catch(
        () => undefined,
      )
    },
  })
}

export function useUpdateQuestionBankEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreateQuestionBankEntryInput }) =>
      updateQuestionBankEntry(id, input),
    onSuccess: () => {
      Promise.resolve(qc.invalidateQueries({ queryKey: questionBankKeys.all })).catch(
        () => undefined,
      )
    },
  })
}

export function useDeleteQuestionBankEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteQuestionBankEntry,
    onSuccess: () => {
      Promise.resolve(qc.invalidateQueries({ queryKey: questionBankKeys.all })).catch(
        () => undefined,
      )
    },
  })
}
